import React, { useState } from "react";
import { FiX } from "react-icons/fi";
import axios from "axios";
import { baseurl } from "../baseurl";
import Swal from "sweetalert2";
import GoogleLogin from "./GoogleLogin";

function LoginModal({ show, onClose }) {
  const [activeTab, setActiveTab] = useState("login");
  const [formData, setFormData] = useState({
    name: "",
    identifier: "", // 🔄 Unified state key for Email or Phone
    email: "",      // Used for registration
    phone: "",      // Used for registration
    password: "",
  });
  const [loading, setLoading] = useState(false);

  if (!show) return null;

  // 🟢 Handle input changes
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // 🟢 Handle Login
  const handleLogin = async (e) => {
    e.preventDefault();
    const { identifier, password } = formData; // 🔄 Extract identifier

    if (!identifier || !password) {
      return Swal.fire({
        icon: "warning",
        title: "Missing Fields",
        text: "Please enter your Email/Phone and password.",
      });
    }

    try {
      setLoading(true);
      // 🔄 Sending 'identifier' to the backend payload instead of just 'email'
      const res = await axios.post(`${baseurl}userLogin`, { identifier, password });

      if (res.status === 200) {
        const { id, uuid, name } = res.data.user;

        // Store user session
        localStorage.setItem("customerId", id);
        localStorage.setItem("customerUuid", uuid);
        localStorage.setItem("user", JSON.stringify(res.data.user));

        Swal.fire({
          icon: "success",
          title: "Welcome Back!",
          text: `Hi ${name || "User"}, glad to see you again.`,
          timer: 1500,
          showConfirmButton: false,
        });

        // Close modal and reload after short delay
        setTimeout(() => {
          onClose();
          window.location.reload();
        }, 1200);
      }
    } catch (err) {
      console.error("Login Error:", err);

      Swal.fire({
        icon: "error",
        title: "Login Failed",
        text: err.response?.data?.error || "Invalid credentials or server error.",
      });
    } finally {
      setLoading(false);
    }
  };

  // 🟢 Handle Register
  const handleRegister = async (e) => {
    e.preventDefault();
    const { name, email, phone, password } = formData;

    if (!name || !email || !phone || !password) {
      return Swal.fire({
        icon: "warning",
        title: "Missing Fields",
        text: "All fields are required for registration.",
      });
    }

    try {
      setLoading(true);
      const res = await axios.post(`${baseurl}userRegister`, {
        name,
        email,
        phone,
        password,
      });

      if (res.status === 200) {
        const { id, uuid } = res.data;

        localStorage.setItem("customerId", id);
        localStorage.setItem("customerUuid", uuid);

        Swal.fire({
          icon: "success",
          title: "Account Created Successfully 🎉",
          text: "Welcome aboard! Your account has been created.",
          timer: 1500,
          showConfirmButton: false,
        });

        onClose();
        setTimeout(() => {
          window.location.reload();
        }, 1600);
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Registration Failed",
        text: err.response?.data?.error || "Something went wrong. Try again later.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        {/* Header */}
        <div className="modal-header">
          <div className="tabs">
            <button
              className={`tab-btn ${activeTab === "login" ? "active" : ""}`}
              onClick={() => setActiveTab("login")}
            >
              Login
            </button>
            <button
              className={`tab-btn ${activeTab === "register" ? "active" : ""}`}
              onClick={() => setActiveTab("register")}
            >
              Register
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="modal-body p-0">
          {activeTab === "login" && (
            <form onSubmit={handleLogin}>
              {/* 🔄 Input updated to accept Email or Phone dynamically */}
              <div className="input-group">
                <label>Email or Phone Number</label>
                <input
                  type="text"
                  name="identifier"
                  placeholder="Enter your email or phone"
                  value={formData.identifier}
                  onChange={handleChange}
                />
              </div>
              <div className="input-group">
                <label>Password</label>
                <input
                  type="password"
                  name="password"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                />
              </div>
              
              <div>
                <a href="/forgot-password" target="_blank">Forgot Password?</a>
              </div>

              <button type="submit" className="btn login-btn" disabled={loading}>
                {loading ? "Logging in..." : "Login"}
              </button>
            </form>
          )}

          {activeTab === "register" && (
            <form onSubmit={handleRegister}>
              <div className="input-group">
                <label>Name</label>
                <input
                  type="text"
                  name="name"
                  placeholder="Enter your name"
                  value={formData.name}
                  onChange={handleChange}
                />
              </div>
              <div className="input-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
              <div className="input-group">
                <label>Phone</label>
                <input
                  type="tel"
                  name="phone"
                  placeholder="Enter your phone number"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
              <div className="input-group">
                <label>Password</label>
                <input
                  type="password"
                  name="password"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                />
              </div>
              <button
                type="submit"
                className="btn register-btn"
                disabled={loading}
              >
                {loading ? "Registering..." : "Register"}
              </button>
            </form>
          )}

          <div className="separator">or</div>
          <GoogleLogin/>

          <div className="text-end">
            <button className="close-btn btn btn-primary" onClick={onClose} >Close <FiX size={24}  /></button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginModal;